import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { sheetFetch } from '@/lib/sheetFetch'
import type { TaskComment } from '@/types/work'

// Comment thread for a task. All mutations (create / update / destroy)
// go through sheetFetch — a single fetch path that works in both the
// detail sheet AND the standalone /work/tasks/:id page. The caller
// supplies onAfterMutation, which is what differentiates the two
// contexts:
//
//   - Sheet (TaskDetailSheet): refetches the sheet's JSON and reloads
//     the index list behind it.
//   - Standalone Show.tsx: router.reload() to rerender the page with
//     the new comment list.
//
// Either way the controller's HTML redirect is bypassed (the JSON
// branch returns 200/422), so neither context ever navigates to a
// different page mid-mutation.

interface CommentThreadProps {
  taskId: number
  comments: TaskComment[]
  // Mirrors task.policy_caps.comment — false on closed tasks
  // (cancelled / archived). The new-comment form is hidden when false;
  // the server is still authoritative (CreateTaskComment rejects).
  canComment: boolean
  // Called after every successful mutation so the caller can refresh
  // its own state (sheet refetch, page reload, etc).
  onAfterMutation: () => void
}

export function CommentThread({ taskId, comments, canComment, onAfterMutation }: CommentThreadProps) {
  return (
    <div className="space-y-4">
      {comments.length === 0 ? (
        <p className="text-sm text-muted-foreground">Aucun commentaire pour le moment</p>
      ) : (
        <ul className="space-y-3">
          {comments.map((c) => (
            <CommentItem key={c.id} taskId={taskId} comment={c} onAfterMutation={onAfterMutation} />
          ))}
        </ul>
      )}

      {canComment && <NewCommentForm taskId={taskId} onAfterMutation={onAfterMutation} />}

      {!canComment && comments.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Cette tâche est clôturée — aucun nouveau commentaire ne peut être ajouté.
        </p>
      )}
    </div>
  )
}

function CommentItem({
  taskId,
  comment,
  onAfterMutation,
}: {
  taskId: number
  comment: TaskComment
  onAfterMutation: () => void
}) {
  const [editing, setEditing] = useState(false)

  // Tombstones: server returns body=null when discarded_at is set.
  // can_update / can_destroy are false on tombstones too — render
  // a neutral italicized placeholder.
  if (comment.body === null) {
    return (
      <li className="rounded-md border bg-muted/40 px-3 py-2 text-sm italic text-muted-foreground">
        Commentaire supprimé
      </li>
    )
  }

  if (editing) {
    return (
      <EditCommentForm
        taskId={taskId}
        comment={comment}
        onCancel={() => setEditing(false)}
        onSaved={() => {
          setEditing(false)
          onAfterMutation()
        }}
      />
    )
  }

  return (
    <li className="rounded-md border bg-card px-3 py-2">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium">{comment.author_name ?? '—'}</span>
        <span className="text-xs text-muted-foreground">
          {new Date(comment.created_at).toLocaleString('fr-FR')}
          {comment.edited_at && ' · modifié'}
        </span>
      </div>
      <p className="mt-1 whitespace-pre-line text-sm">{comment.body}</p>

      {(comment.can_update || comment.can_destroy) && (
        <div className="mt-2 flex gap-2">
          {comment.can_update && (
            <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
              Modifier
            </Button>
          )}
          {comment.can_destroy && (
            <DeleteCommentButton
              taskId={taskId}
              commentId={comment.id}
              onAfterMutation={onAfterMutation}
            />
          )}
        </div>
      )}
    </li>
  )
}

function NewCommentForm({
  taskId,
  onAfterMutation,
}: {
  taskId: number
  onAfterMutation: () => void
}) {
  const [body, setBody] = useState('')
  const [processing, setProcessing] = useState(false)
  const [errors, setErrors] = useState<{ body?: string; base?: string }>({})

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setProcessing(true)
    setErrors({})
    try {
      const response = await sheetFetch(`/work/tasks/${taskId}/comments`, {
        method: 'POST',
        body: { comment: { body } },
      })
      if (response.ok) {
        setBody('')
        onAfterMutation()
      } else if (response.status === 422) {
        const json = (await response.json()) as { errors?: Record<string, string> }
        setErrors(json.errors ?? { base: "Impossible d'ajouter le commentaire." })
      } else {
        setErrors({ base: 'Une erreur est survenue.' })
      }
    } catch {
      setErrors({ base: "Impossible d'ajouter le commentaire." })
    } finally {
      setProcessing(false)
    }
  }

  return (
    <form onSubmit={submit} className="space-y-2">
      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Ajouter un commentaire…"
        rows={3}
      />
      {errors.body && <p className="text-xs text-destructive">{errors.body}</p>}
      {errors.base && <p className="text-xs text-destructive">{errors.base}</p>}
      <div className="flex justify-end">
        <Button type="submit" size="sm" disabled={processing || body.trim() === ''}>
          Publier
        </Button>
      </div>
    </form>
  )
}

function EditCommentForm({
  taskId,
  comment,
  onCancel,
  onSaved,
}: {
  taskId: number
  comment: TaskComment
  onCancel: () => void
  onSaved: () => void
}) {
  const [body, setBody] = useState(comment.body ?? '')
  const [processing, setProcessing] = useState(false)
  const [errors, setErrors] = useState<{ body?: string; base?: string }>({})

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setProcessing(true)
    setErrors({})
    try {
      const response = await sheetFetch(`/work/tasks/${taskId}/comments/${comment.id}`, {
        method: 'PATCH',
        body: { comment: { body } },
      })
      if (response.ok) {
        onSaved()
      } else if (response.status === 422) {
        const json = (await response.json()) as { errors?: Record<string, string> }
        setErrors(json.errors ?? { base: 'Impossible de mettre à jour le commentaire.' })
      } else {
        setErrors({ base: 'Une erreur est survenue.' })
      }
    } catch {
      setErrors({ base: 'Impossible de mettre à jour le commentaire.' })
    } finally {
      setProcessing(false)
    }
  }

  return (
    <form onSubmit={submit} className="space-y-2 rounded-md border bg-card px-3 py-2">
      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={3}
      />
      {errors.body && <p className="text-xs text-destructive">{errors.body}</p>}
      {errors.base && <p className="text-xs text-destructive">{errors.base}</p>}
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Annuler
        </Button>
        <Button type="submit" size="sm" disabled={processing}>
          Enregistrer
        </Button>
      </div>
    </form>
  )
}

function DeleteCommentButton({
  taskId,
  commentId,
  onAfterMutation,
}: {
  taskId: number
  commentId: number
  onAfterMutation: () => void
}) {
  const [deleting, setDeleting] = useState(false)

  async function handleDelete() {
    if (!confirm('Supprimer ce commentaire ?')) return
    setDeleting(true)
    try {
      // fetch resolves on 4xx/5xx — only fire onAfterMutation when the
      // server actually accepted the delete. Otherwise the parent would
      // refresh expecting the comment to be gone.
      const response = await sheetFetch(`/work/tasks/${taskId}/comments/${commentId}`, {
        method: 'DELETE',
      })
      if (response.ok) {
        onAfterMutation()
      } else {
        alert('Impossible de supprimer le commentaire.')
      }
    } catch {
      alert('Impossible de supprimer le commentaire.')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Button size="sm" variant="ghost" onClick={handleDelete} disabled={deleting}>
      Supprimer
    </Button>
  )
}
